import { Spinner } from "@heroui/react";
import { MapPin } from "lucide-react";
import { useGeolocation } from "@/hooks/use-geolocation";
import { useDropoffLocations } from "@/api/use-dropoff-locations";
import type { WeeklyHours } from "@/api.types.v2";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface DropoffLocationListProps {
  carrierId: string;
  shipFrom?: { lat: number; lng: number } | null;
}

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type Weekday = (typeof WEEKDAYS)[number];

/** Format the schedule as a compact summary line:
 *  "Mon–Fri 9–17, Sat 10–16" (collapses contiguous identical windows). */
function formatTodayHoursSummary(
  hours: WeeklyHours | null,
  t: TFunction
): string {
  if (!hours) return t("dropoffLocations.hoursNotListed");
  const groups: { days: Weekday[]; key: string }[] = [];
  for (const day of WEEKDAYS) {
    const window = hours[day];
    const key = window ? `${window.open}-${window.close}` : "closed";
    if (key === "closed") continue;
    const last = groups[groups.length - 1];
    if (last?.key === key) {
      last.days.push(day);
    } else {
      groups.push({ days: [day], key });
    }
  }
  if (groups.length === 0) return t("dropoffLocations.closed");
  return groups
    .map((g) => {
      const label =
        g.days.length === 1
          ? t(`dropoffLocations.weekdays.${g.days[0]}`)
          : `${t(`dropoffLocations.weekdays.${g.days[0]}`)}–${t(
              `dropoffLocations.weekdays.${g.days[g.days.length - 1]}`
            )}`;
      const [open, close] = g.key.split("-");
      // Strip leading zero + ":00" for compactness ("09:00" → "9", "16:30" stays "16:30")
      const compact = (t: string) =>
        t.endsWith(":00")
          ? String(Number.parseInt(t.split(":")[0], 10))
          : t.replace(/^0/, "");
      return `${label} ${compact(open)}–${compact(close)}`;
    })
    .join(", ");
}

/** Format "HH:MM" with am/pm for display in the open-now badge. */
function formatTime(hhmm: string | null): string {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const dh = h % 12 || 12;
  return m === 0
    ? `${dh}${period}`
    : `${dh}:${m.toString().padStart(2, "0")}${period}`;
}

/** Format opensAtLocal which can be "HH:MM" (same day), "tomorrow HH:MM", or
 *  "Tuesday HH:MM" — convert any trailing time to am/pm. */
function formatOpensAt(opensAt: string | null, t: TFunction): string {
  if (!opensAt) return t("dropoffLocations.later");
  const m = /^(.*?)(\d{2}:\d{2})$/.exec(opensAt);
  if (!m) return opensAt;
  const rawPrefix = m[1].trim();
  const prefix =
    rawPrefix.toLowerCase() === "tomorrow"
      ? t("pickup.date.tomorrow")
      : rawPrefix;
  const time = formatTime(m[2]);
  return prefix ? `${prefix} ${time}` : time;
}

export function DropoffLocationList({
  carrierId,
  shipFrom,
}: DropoffLocationListProps) {
  const { t } = useTranslation("app");
  const { coords: geoCoords, status: geoStatus } = useGeolocation();
  const origin = geoStatus === "resolved" ? geoCoords : (shipFrom ?? null);

  const enabled = geoStatus !== "pending";
  const { data, isLoading, isError } = useDropoffLocations(
    enabled ? carrierId : undefined,
    origin
  );

  if (!enabled || isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-default-600 mt-3">
        <Spinner size="sm" color="primary" />
        {t("dropoffLocations.loading")}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-danger mt-3">
        {t("dropoffLocations.loadError")}
      </p>
    );
  }

  if (!data || data.length === 0) {
    return (
      <p className="text-sm text-default-600 mt-3">
        {t("dropoffLocations.empty")}
      </p>
    );
  }

  return (
    <ul className="mt-3 space-y-2">
      {data.map((loc) => {
        const addr = loc.address;
        const cityLine = [addr.city, addr.province, addr.postalCode]
          .filter(Boolean)
          .join(", ");
        const street = [addr.line1, addr.line2].filter(Boolean).join(", ");
        const hoursSummary = formatTodayHoursSummary(loc.operatingHours, t);
        return (
          <li
            key={loc.id}
            className="text-sm text-foreground border-t border-default-200 pt-2 first:border-t-0 first:pt-0"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{loc.name}</p>
                {street && <p>{street}</p>}
                {cityLine && <p>{cityLine}</p>}
                <p className="text-tiny mt-1 text-default-500">
                  <span>{hoursSummary}</span>
                  {hoursSummary !== t("dropoffLocations.hoursNotListed") && (
                    <>
                      <span className="mx-1 text-default-300">·</span>
                      {loc.openNow ? (
                        <span className="font-medium text-success-600">
                          {t("dropoffLocations.open")}
                          {loc.closesAtLocal
                            ? ` ${t("dropoffLocations.until", {
                                time: formatTime(loc.closesAtLocal),
                              })}`
                            : ""}
                        </span>
                      ) : (
                        <span className="text-default-500">
                          {t("dropoffLocations.closed")}
                          {loc.opensAtLocal
                            ? ` · ${t("dropoffLocations.opens", {
                                time: formatOpensAt(loc.opensAtLocal, t),
                              })}`
                            : ""}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
              {loc.distanceKm !== undefined && (
                <span className="text-xs text-default-500 flex items-center gap-1 shrink-0">
                  <MapPin className="h-3 w-3" />
                  {loc.distanceKm.toFixed(1)} km
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
